import type { Language } from '@glintfans/shared';

/** AiLumix UI copy: English + 繁體中文. Keys are stable identifiers so the
 *  same dictionary can be reused by the future mobile / desktop clients. */
export interface I18nDict {
  appName: string;
  nav: Record<
    'markets' | 'discover' | 'futures' | 'chart' | 'news' | 'calendar' | 'watchlist' | 'alerts' | 'portfolio' | 'ai' | 'signals' | 'hyperliquid' | 'research' | 'profile' | 'more' | 'notifications' | 'settings' | 'workspace' | 'help' | 'strategy',
    string
  >;
  markets: Record<string, string>;
  detail: Record<string, string>;
  rank: Record<string, string>;
  watchlist: Record<string, string>;
  alerts: Record<string, string>;
  notifications: Record<string, string>;
  news: Record<string, string>;
  auth: Record<string, string>;
  portfolio: Record<string, string>;
  profile: Record<string, string>;
  common: Record<string, string>;
  research: Record<string, string>;
  health: Record<string, string>;
  help: Record<string, string>;
}

export type Lang = 'en' | 'zh-Hant' | 'zh-Hans';
export type CopyKey = keyof I18nDict;

export const copy: Record<Lang, I18nDict> = {
  en: {
    appName: 'AiLumix',
    nav: {
      markets: 'Markets',
      discover: 'Discover',
      futures: 'Futures',
      signals: 'Signals',
      chart: 'Analysis',
      news: 'News',
      calendar: 'Calendar',
      watchlist: 'Watchlist',
      alerts: 'Alerts',
      portfolio: 'Portfolio',
      hyperliquid: 'Hyperliquid',
      research: 'Research',
      ai: 'AI',
      more: 'More',
      notifications: 'Notifications',
      settings: 'Settings',
      profile: 'Profile',
      workspace: 'My Research',
      help: 'Help',
      strategy: 'Strategies',
    },
    markets: {
      title: 'Markets',
      overview: 'Market Overview',
      totalCap: 'Total Market Cap',
      volume24h: '24h Turnover',
      btcDominance: 'BTC Dominance',
      ethDominance: 'ETH Dominance',
      fearGreed: 'Fear & Greed',
      tabFavorites: 'Favorites',
      tabSpot: 'Spot',
      tabFutures: 'Futures',
      tabNew: 'New Listings',
      tabTrending: 'Trending',
      tabAll: 'Market Cap',
      ovTotalOi: 'Total Open Interest',
      ovLongShort: 'Long/Short Ratio',
      ovLiquidation: '24h Liquidation',
      ovStablecap: 'Stablecoin Market Cap',
      ovAvgFunding: 'Avg Funding Rate',
      breadth: 'Market Breadth',
      breadthUp: 'Up',
      breadthDown: 'Down',
      breadthFlat: 'Flat',
      moreData: 'More market data',
      overviewCollapse: 'Market Overview',
      sortBy: 'Sort by',
      assetLevelRank: 'Asset-level cap (deduped by base asset)',
      marketLevelRank: 'Market-level contracts (per exchange)',
      dataHealthHint: 'Coverage & update time',
      onlyAnomalies: 'Only anomalies',
      undo: 'Undo',
      removed: 'Removed',
      research: 'Research',
      colSymbol: 'Name',
      colPrice: 'Price',
      colChange: '24h %',
      colHigh: '24h High',
      colLow: '24h Low',
      colVolume: '24h Turnover',
      searchPlaceholder: 'Search coin / symbol',
      noResults: 'No results found',
      rankings: 'Rankings',
      addWatch: '☆',
      added: '✓',
      loginRequired: 'Log in to add to watchlist',
    },
    detail: {
      kline: 'Price Chart',
      orderBook: 'Order Book',
      trades: 'Recent Trades',
      marketStats: 'Market Stats',
      about: 'About',
      high24h: '24h High',
      low24h: '24h Low',
      volume24h: '24h Volume',
      marketCap: 'Market Cap',
      symbol: 'Symbol',
      baseAsset: 'Base Asset',
      quoteAsset: 'Quote Asset',
      addWatchlist: 'Add to Watchlist',
      addedWatchlist: 'Added to Watchlist',
      createAlert: 'Create Alert',
      buy: 'Bids',
      sell: 'Asks',
      time: 'Time',
      price: 'Price',
      amount: 'Amount',
      derivatives: 'Derivatives',
      fundingRate: 'Funding Rate',
      nextFunding: 'Next Funding',
      openInterest: 'Open Interest',
      openInterestUsd: 'OI (USD)',
      longShortRatio: 'Long/Short Ratio',
      annualized: 'Annualized',
      fundamentals: 'Fundamentals',
      rank: 'Rank',
      supply: 'Circulating Supply',
      totalSupply: 'Total Supply',
      ath: 'All-Time High',
      atl: 'All-Time Low',
      fundingHistory: 'Funding History',
      oiHistory: 'OI History',
      contractSpec: 'Contract Spec',
      leverage: 'Leverage',
      minQty: 'Min Qty',
      tickSize: 'Tick Size',
      ctVal: 'Face Value',
      history: 'History',
    },
    rank: {
      rankGainers: 'Top Gainers',
      rankLosers: 'Top Losers',
      rankVolume: 'Volume',
      rankTrending: 'Trending',
      rankNew: 'New Listings',
      rankVolatility: 'Volatility',
      rankOi: 'Open Interest',
      rankFunding: 'Funding Rate',
      rankColRank: '#',
      rankColSymbol: 'Symbol',
      rankColPrice: 'Price',
      rankColChange: '24h %',
      rankColVolume: '24h Volume',
      rankColFunding: 'Funding',
      rankColOi: 'OI (USD)',
      rankColRatio: 'Long/Short',
    },
    watchlist: {
      title: 'Watchlist',
      newGroup: 'New Group',
      groupName: 'Group Name',
      empty: 'Your watchlist is empty. Add coins from the markets page.',
      sessionExpired: 'Session expired. Please log in again.',
      remove: 'Remove',
      addSymbol: 'Add Symbol',
      symbolPlaceholder: 'e.g. BTCUSDT',
      defaultGroup: 'Default',
      sort: 'Sort',
      sortManual: 'Manual',
      sortPrice: 'Price',
      sortChange: 'Change',
      sortVolume: 'Volume',
      addCoin: 'Add Coin',
      groupEmpty: 'This group is empty',
      deleteGroup: 'Delete Group',
      deleteGroupConfirm: 'Delete this group? Coins in it will be removed.',
      searchHint: 'Type to search coins or trading pairs',
      alreadyAdded: 'Added',
      add: 'Add',
    },
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all read',
      all: 'All',
      unread: 'Unread',
      empty: 'No notifications',
      emptyUnread: 'All caught up',
      typePrice: 'Price Alert',
      typeSignal: 'Market Signal',
      typeNews: 'News',
      typeSystem: 'System',
      typeAccount: 'Account',
      typeAi: 'AI Alert',
      view: 'View',
      markRead: 'Mark read',
      delete: 'Delete',
      loginRequired: 'Please sign in to view notifications',
    },
    alerts: {
      title: 'Market Alerts',
      newAlert: 'New Alert',
      symbol: 'Symbol',
      condition: 'Condition',
      target: 'Target',
      status: 'Status',
      active: 'Active',
      triggered: 'Triggered',
      disabled: 'Disabled',
      priceAbove: 'Price above',
      priceBelow: 'Price below',
      changeAbove: 'Change % above',
      changeBelow: 'Change % below',
      volumeSpike: 'Volume spike',
      crossAbove: 'Cross above',
      crossBelow: 'Cross below',
      fundingAbove: 'Funding rate above',
      fundingBelow: 'Funding rate below',
      oiAbove: 'Open interest above',
      oiBelow: 'Open interest below',
      rsiAbove: 'RSI above',
      rsiBelow: 'RSI below',
      invalidNumber: 'Enter a valid number',
      minValue: 'Min',
      maxValue: 'Max',
      create: 'Create',
      cancel: 'Cancel',
      loginRequired: 'Please log in to create alerts.',
      error: 'Failed to create alert.',
      empty: 'No alerts yet. Create one above.',
      filterAll: 'All',
      filterActive: 'Active',
      filterTriggered: 'Triggered',
      searchSymbol: 'Search symbol',
      deleteAlert: 'Delete',
      confirmDelete: 'Delete this alert?',
      confirmDiscard: 'Discard unsaved changes?',
      summary: 'Notify me when',
      browserAllowed: 'Allowed',
      browserDefault: 'Not authorized',
      browserDenied: 'Blocked',
      browserUnsupported: 'Unsupported',
      groupPrice: 'Price',
      groupChange: 'Change',
      groupVolume: 'Volume',
      groupCross: 'Cross',
      groupFunding: 'Funding',
      groupOi: 'Open Interest',
      groupRsi: 'RSI',
      summaryValue: 'Target value',
      interval: 'Period',
      currentValue: 'Current',
      lastChecked: 'Last checked',
      oneShot: 'One-shot',
      oneShotHint: 'Stop after first trigger',
      repeat: 'Repeat',
      repeatHint: 'Keep watching after trigger',
      noCurrent: '—',
      venueLabel: 'Venue',
      productLabel: 'Product',
      snapshot: 'Trigger snapshot',
      pause: 'Pause',
      resume: 'Resume',
      edit: 'Edit',
      update: 'Update',
    },
    news: {
      title: 'News',
      source: 'Source',
      readMore: 'Read more',
      expand: 'Expand',
      collapse: 'Collapse',
      flashTip: 'Flash-style feed — summaries expand inline, no page jumps.',
      catLatest: 'Latest',
      catBreaking: 'Breaking',
      catBitcoin: 'Bitcoin',
      catEthereum: 'Ethereum',
      catAltcoins: 'Altcoins',
      catMacro: 'Macro',
      catExchange: 'Exchange',
      catRegulation: 'Regulation',
      empty: 'No news yet.',
    },
    auth: {
      login: 'Log In',
      register: 'Sign Up',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      displayName: 'Display Name',
      logout: 'Log Out',
      sessionExpired: 'Session Expired · Sign In',
      welcomeBack: 'Welcome Back',
      loginHint: 'Sign in to manage your watchlist, alerts and positions',
      alreadyLoggedIn: 'You are already signed in',
      backToMarkets: 'Back to Markets',
      goToAccount: 'Go to Account',
      loggingIn: 'Signing in...',
      createAccount: 'Create Account',
      registering: 'Creating account...',
      noAccount: 'No account? Sign up',
      hasAccount: 'Already have an account? Log in',
      verificationCode: 'Verification Code',
      sendCode: 'Send Code',
      resendIn: 'Resend in {s}s',
      codeSent: 'Code sent',
      randomUsernameHint: 'A username is generated automatically; you can change it later in your profile.',
      emailPlaceholder: 'Enter your email',
      passwordPlaceholder: 'Enter your password',
      confirmPasswordPlaceholder: 'Confirm your password',
      codePlaceholder: 'Enter the verification code',
    },
    portfolio: {
      title: 'Portfolio',
      addPosition: 'Add Position',
      symbol: 'Symbol',
      amount: 'Amount',
      avgBuyPrice: 'Avg Buy Price',
      currentValue: 'Current Value',
      cost: 'Cost',
      pnl: 'PnL',
      allocation: 'Allocation',
      totalValue: 'Total Value',
      totalCost: 'Total Cost',
      totalPnl: 'Total PnL',
      settledRate: 'Settled Return',
      empty: 'No positions yet. Add BTC, ETH, SOL and more.',
      remove: 'Remove',
      loginRequired: 'Please log in to manage your portfolio.',
    },
    profile: {
      title: 'Profile & Settings',
      language: 'Language',
      theme: 'Theme',
      dark: 'Dark',
      light: 'Light',
      displayName: 'Display Name',
      email: 'Email',
      notificationChannel: 'Notification Channels',
      role: 'Role',
      save: 'Save',
      saved: 'Saved',
      avatar: 'Avatar',
      avatarDefault: 'Default',
      avatarUpload: 'Upload Avatar',
      avatarUploadHint: 'Click the avatar or the button below to upload a local image (PNG / JPG / WEBP / GIF, up to 3MB).',
      avatarChange: 'Change',
      avatarSaving: 'Uploading...',
      avatarInvalid: 'Only PNG, JPG, WEBP or GIF images are allowed (max 3MB).',
      changeEmail: 'Change Email',
      changeEmailHint: 'A verification code will be sent to your current email, and another to the new email.',
      newEmail: 'New Email',
      newEmailCode: 'Code sent to new email',
      oldEmailCode: 'Code sent to current email',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      changePassword: 'Change Password',
      updatedPassword: 'Password updated. Other devices have been signed out.',
      emailChanged: 'Email updated',
      accountCard: 'Account',
      emailCard: 'Email',
      securityCard: 'Security',
      memberSince: 'Member since',
      defaultNicknameHint: 'You can change your display name here.',
      cancel: 'Cancel',
      confirm: 'Confirm',
      close: 'Close',
    },
    common: {
      loading: 'Loading...',
      apply: 'Apply',
      reset: 'Reset',
      error: 'Something went wrong',
      retry: 'Retry',
      noData: 'No data',
      updated: 'Updated',
      volume: 'Volume',
      change: 'Change',
      price: 'Price',
      market: 'Market',
      new: 'NEW',
    },
    health: {
      title: 'Data Health',
      checking: 'Checking…',
      ok: 'Healthy',
      stale: 'Delayed',
      failed: 'Failed',
      unknown: 'Not collected',
      sourcesOk: 'Data sources healthy',
      sourcesFailed: 'data sources unhealthy',
      sourcesDelayed: 'data sources delayed',
      viewDetails: 'View details',
      close: 'Close',
      lastSuccessAge: 'Last success',
      lastSuccessAt: 'Success time',
      latency: 'Latency',
      coverage: 'Coverage',
      missing: 'Missing',
      consecutiveFailures: 'consecutive failures',
      autoRetry: 'auto retry scheduled',
      checkingStatus: 'Checking data sources…',
      noHealthRecords: 'No health records yet (collectors have not written beats)',
      healthNote: 'Collectors record every success/failure automatically; this shows the real data update time, not the page refresh time.',
      secAgo: 's ago',
      minAgo: 'min ago',
      hourAgo: 'h ago',
      seconds: 's',
      instant: 'instant',
      items: 'items',
      noCoverage: '—',
      descOk: 'Updating at expected frequency',
      descStale: 'Not updated past threshold; may be temporarily unavailable',
      descFailed: 'Last collection failed; system is retrying automatically',
      descUnknown: 'No beat recorded yet (generated after service start)',
      total: 'Total',
      serverTime: 'Server time',
      emptyDesc: 'No health records yet. Collectors write beats on every success/failure; entries appear within minutes after service restart.',
      colMetric: 'Metric',
      colMarket: 'Market',
      colStatus: 'Status',
      colLastSuccess: 'Last success',
      colLatency: 'Latency',
      colCoverage: 'Coverage',
      colMissing: 'Missing / Reason',
      colRetry: 'Retry',
      normalUpdating: 'Updating normally',
      retryAuto: 'Auto',
      subtitle: 'Shows each data source\'s real update state (not the page refresh time). Failures only affect the relevant module.',
      footerNote: 'Source names and thresholds are configured per data type (real-time quotes / hourly series / calendar).',
      refresh: 'Refresh',
      dataStatus: 'Data status',
      srcTickers: 'Real-time Quotes',
      srcSpotTickers: 'Multi-exchange Spot Quotes',
      srcExternalTickers: 'External Exchange Quotes',
      srcOverview: 'Market Overview',
      srcDerivatives: 'Derivatives Data',
      srcNews: 'News Feed',
      srcCalendar: 'Economic Calendar',
      srcHlOnchain: 'Hyperliquid On-chain',
      srcLiqEvents: 'Liquidation Events',
    },
    help: {
      title: 'Help Center',
      subtitle: 'Find a feature, understand what it solves, and get started in 3–5 steps.',
      searchPlaceholder: 'Search features…',
      allFamilies: 'All',
      entry: 'Entry',
      steps: 'How to use',
      faq: 'FAQ',
      types: 'Signal & alert types',
      backToAll: 'Back to all',
      noResults: 'No matching features. Try a different keyword.',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      cardCount: 'cards',
    },
    research: {
      tab: 'Analysis',
      instrument: 'Instrument',
      venue: 'Exchange',
      product: 'Product',
      metric: 'Metric',
      range: 'Range',
      interval: 'Interval',
      qOk: 'Live',
      qPartial: 'Partial',
      qStale: 'Stale',
      qMissing: 'No data',
      qUnsupported: 'Unavailable',
      qInsufficient: 'Insufficient history',
      qInsufficientDetail: 'History below quality gates (samples ≥ 100 & coverage ≥ 80%)',
      coverage: 'Coverage',
      asOf: 'Updated',
      source: 'Source',
      emptyTitle: 'No analysis data yet',
      emptyDesc: 'Pick a supported instrument and metric to see linked series.',
      metricPrice: 'Price',
      metricPriceDesc: 'Last traded price, USD. Spot or perp per instrument.',
      metricQuoteVolume: 'Quote Volume',
      metricQuoteVolumeDesc: 'Notional traded volume in the period, USD.',
      metricOpenInterest: 'Open Interest',
      metricOpenInterestDesc: 'Outstanding notional, USD. Snapshot at period end.',
      metricFundingRate: 'Funding Rate',
      metricFundingRateDesc: 'Settled funding rate (8h cadence where applicable).',
      metricLiquidation: 'Liquidation',
      metricLiquidationDesc: 'Sum of liquidation notional in the period, USD.',
      metricRelativeReturn: 'Relative Return vs BTC',
      metricRelativeReturnDesc: 'Return spread vs BTC, percentage points.',
      metricBreadth: 'Market Breadth',
      metricBreadthDesc: 'Share of advancing assets, percent.',
      metricHv: 'Historical Volatility',
      metricHvDesc: 'Annualised volatility from OHLC, percent.',
      linkedSeries: 'Linked series',
      seriesPrice: 'Price',
      seriesVolume: 'Volume',
      seriesOi: 'OI',
      seriesFunding: 'Funding',
      seriesLiq: 'Liq.',
    },
  },
  'zh-Hant': {
    appName: 'AiLumix',
    nav: {
      markets: '行情',
      discover: '發現',
      futures: '合約',
      signals: '信號',
      chart: '分析',
      news: '資訊',
      calendar: '日曆',
      watchlist: '自選',
      alerts: '預警',
      portfolio: '資產賬本',
      hyperliquid: '鏈上',
      research: '研究',
      ai: 'AI',
      more: '更多',
      notifications: '通知',
      settings: '設置',
      profile: '帳戶',
      workspace: '我的研究',
      help: '幫助中心',
      strategy: '策略',
    },
    markets: {
      title: '行情',
      overview: '市場概覽',
      totalCap: '總市值',
      volume24h: '24H 成交額',
      btcDominance: 'BTC 佔比',
      ethDominance: 'ETH 佔比',
      fearGreed: '恐懼與貪婪指數',
      tabFavorites: '自選',
      tabSpot: '現貨',
      tabFutures: '合約',
      tabNew: '新幣榜',
      tabTrending: '熱門榜',
      tabAll: '市值',
      ovTotalOi: '全網合約持倉量',
      ovLongShort: '全網多空比',
      ovLiquidation: '24H 爆倉總額',
      ovStablecap: '穩定幣總市值',
      ovAvgFunding: '資金費率均值',
      breadth: '市場寬度',
      breadthUp: '漲',
      breadthDown: '跌',
      breadthFlat: '平',
      moreData: '更多市場數據',
      overviewCollapse: '市場概覽',
      sortBy: '排序',
      assetLevelRank: '資產級市值（按基礎資產去重）',
      marketLevelRank: '市場級合約（分交易所）',
      dataHealthHint: '多交易所覆蓋範圍與更新時間',
      onlyAnomalies: '僅看異常',
      undo: '撤銷',
      removed: '已移除',
      research: '研究',
      colSymbol: '名稱',
      colPrice: '價格',
      colChange: '24H 漲跌',
      colHigh: '24H 最高',
      colLow: '24H 最低',
      colVolume: '24H 成交額',
      searchPlaceholder: '搜索幣種 / 交易對',
      noResults: '未找到結果',
      rankings: '排行榜',
      addWatch: '☆',
      added: '✓',
      loginRequired: '登入後可加入自選',
    },
    detail: {
      kline: '價格圖表',
      orderBook: '委託簿',
      trades: '最新成交',
      marketStats: '市場統計',
      about: '關於',
      high24h: '24H 最高',
      low24h: '24H 最低',
      volume24h: '24H 成交量',
      marketCap: '市值',
      symbol: '交易對',
      baseAsset: '基礎資產',
      quoteAsset: '計價資產',
      addWatchlist: '加入自選',
      addedWatchlist: '已加入自選',
      createAlert: '建立預警',
      buy: '買單',
      sell: '賣單',
      time: '時間',
      price: '價格',
      amount: '數量',
      derivatives: '合約數據',
      fundingRate: '資金費率',
      nextFunding: '下次結算',
      openInterest: '持倉量',
      openInterestUsd: '持倉量 (USD)',
      longShortRatio: '多空比',
      annualized: '年化',
      fundamentals: '幣種基本面',
      rank: '排名',
      supply: '流通供應量',
      totalSupply: '總供應量',
      ath: '歷史新高',
      atl: '歷史新低',
      fundingHistory: '資金費率歷史',
      oiHistory: '持倉量歷史',
      contractSpec: '合約規格',
      leverage: '槓桿',
      minQty: '最小下單',
      tickSize: '最小价差',
      ctVal: '面值',
      history: '歷史',
    },
    rank: {
      rankGainers: '漲幅榜',
      rankLosers: '跌幅榜',
      rankVolume: '成交額榜',
      rankTrending: '熱門榜',
      rankNew: '新幣榜',
      rankVolatility: '波動率榜',
      rankOi: '持倉量榜',
      rankFunding: '資金費率榜',
      rankColRank: '#',
      rankColSymbol: '交易對',
      rankColPrice: '價格',
      rankColChange: '24H %',
      rankColVolume: '24H 成交量',
      rankColFunding: '資金費率',
      rankColOi: '持倉量 (USD)',
      rankColRatio: '多空比',
    },
    watchlist: {
      title: '自選',
      newGroup: '新增分組',
      groupName: '分組名稱',
      empty: '自選列表爲空，請從行情頁添加幣種。',
      sessionExpired: '登入已過期，請重新登入',
      remove: '移除',
      addSymbol: '添加幣種',
      symbolPlaceholder: '例如 BTCUSDT',
      defaultGroup: '默認',
      sort: '排序',
      sortManual: '手動',
      sortPrice: '價格',
      sortChange: '漲跌',
      sortVolume: '成交量',
      addCoin: '添加幣種',
      groupEmpty: '此分組爲空',
      deleteGroup: '刪除分組',
      deleteGroupConfirm: '確認刪除該分組？組內幣種將被移除。',
      searchHint: '輸入幣種或交易對開始搜索',
      alreadyAdded: '已添加',
      add: '添加',
    },
    notifications: {
      title: '通知中心',
      markAllRead: '全部標爲已讀',
      all: '全部',
      unread: '未讀',
      empty: '暫無通知',
      emptyUnread: '已全部讀完',
      typePrice: '價格警報',
      typeSignal: '市場信號',
      typeNews: '新聞',
      typeSystem: '系統',
      typeAccount: '帳戶',
      typeAi: 'AI 提示',
      view: '查看',
      markRead: '標爲已讀',
      delete: '刪除',
      loginRequired: '請先登入查看通知',
    },
    alerts: {
      title: '市場預警',
      newAlert: '新增預警',
      symbol: '交易對',
      condition: '條件',
      target: '目標值',
      status: '狀態',
      active: '生效中',
      triggered: '已觸發',
      disabled: '已停用',
      priceAbove: '價格高於',
      priceBelow: '價格低於',
      changeAbove: '漲跌幅高於',
      changeBelow: '漲跌幅低於',
      volumeSpike: '成交量突增',
      crossAbove: '上穿',
      crossBelow: '下穿',
      fundingAbove: '資金費率高於',
      fundingBelow: '資金費率低於',
      oiAbove: '持倉量高於',
      oiBelow: '持倉量低於',
      rsiAbove: 'RSI高於',
      rsiBelow: 'RSI低於',
      invalidNumber: '請輸入有效數字',
      minValue: '不能小於',
      maxValue: '不能大於',
      create: '建立',
      cancel: '取消',
      loginRequired: '請先登入後建立預警。',
      error: '建立預警失敗。',
      empty: '暫無預警，請點擊「建立預警」。',
      filterAll: '全部',
      filterActive: '生效中',
      filterTriggered: '已觸發',
      searchSymbol: '搜索交易對',
      deleteAlert: '刪除',
      confirmDelete: '確認刪除該預警？',
      confirmDiscard: '有未儲存的變更，確定放棄？',
      summary: '當',
      browserAllowed: '已允許',
      browserDefault: '未授權',
      browserDenied: '已阻止',
      browserUnsupported: '不支援',
      groupPrice: '價格',
      groupChange: '漲跌',
      groupVolume: '成交量',
      groupCross: '穿越',
      groupFunding: '資金費率',
      groupOi: '持倉量',
      groupRsi: 'RSI',
      summaryValue: '目標值',
      interval: '計算週期',
      currentValue: '當前值',
      lastChecked: '最近評估',
      oneShot: '一次性',
      oneShotHint: '觸發後自動停用',
      repeat: '重複',
      repeatHint: '觸發後持續監控',
      noCurrent: '—',
      venueLabel: '交易所',
      productLabel: '產品',
      snapshot: '觸發快照',
      pause: '暫停',
      resume: '恢復',
      edit: '編輯',
      update: '更新',
    },
    news: {
      title: '資訊',
      source: '來源',
      readMore: '閱讀更多',
      expand: '展開',
      catLatest: '最新',
      catBreaking: '突發',
      catBitcoin: '比特幣',
      catEthereum: '以太坊',
      catAltcoins: '山寨幣',
      catMacro: '宏觀',
      catExchange: '交易所',
      catRegulation: '監管',
      collapse: '收起',
      flashTip: '快訊式資訊流 — 摘要就地展開，不跳轉新頁。',
      empty: '暫無資訊。',
    },
    auth: {
      login: '登入',
      register: '註冊',
      email: '信箱',
      password: '密碼',
      confirmPassword: '確認密碼',
      displayName: '暱稱',
      logout: '登出',
      sessionExpired: '登入已失效 · 重新登入',
      welcomeBack: '歡迎回來',
      loginHint: '登入以管理自選、預警與持倉記錄',
      alreadyLoggedIn: '你已登入',
      backToMarkets: '返回市場',
      goToAccount: '進入帳戶中心',
      loggingIn: '登入中...',
      createAccount: '建立帳戶',
      registering: '建立帳戶中...',
      noAccount: '沒有帳戶？立即註冊',
      hasAccount: '已有帳戶？立即登入',
      verificationCode: '驗證碼',
      sendCode: '發送驗證碼',
      resendIn: '{s} 秒後重發',
      codeSent: '驗證碼已發送',
      randomUsernameHint: '系統將自動生成用戶名，之後可在帳戶中心修改。',
      emailPlaceholder: '請輸入信箱',
      passwordPlaceholder: '請輸入密碼',
      confirmPasswordPlaceholder: '請再次輸入密碼',
      codePlaceholder: '請輸入驗證碼',
    },
    portfolio: {
      title: '資產賬本',
      addPosition: '新增持倉',
      symbol: '交易對',
      amount: '數量',
      avgBuyPrice: '平均買入價',
      currentValue: '當前價值',
      cost: '成本',
      pnl: '盈虧',
      allocation: '佔比',
      totalValue: '總價值',
      totalCost: '總成本',
      totalPnl: '總盈虧',
      settledRate: '結算收益率',
      empty: '尚無持倉，請添加 BTC、ETH、SOL 等幣種。',
      remove: '移除',
      loginRequired: '請先登入以管理資產。',
    },
    profile: {
      title: '帳戶與設定',
      language: '語言',
      theme: '主題',
      dark: '深色',
      light: '淺色',
      displayName: '暱稱',
      email: '信箱',
      role: '角色',
      notificationChannel: '通知渠道',
      save: '儲存',
      saved: '已儲存',
      avatar: '頭像',
      avatarDefault: '默認',
      avatarUpload: '上傳頭像',
      avatarUploadHint: '點擊頭像或下方按鈕上傳本地圖片（PNG / JPG / WEBP / GIF，最大 3MB）。',
      avatarChange: '更換',
      avatarSaving: '上傳中...',
      avatarInvalid: '僅支持 PNG、JPG、WEBP 或 GIF 圖片（最大 3MB）。',
      changeEmail: '更換信箱',
      changeEmailHint: '將向當前信箱與新信箱各發送一封驗證碼郵件。',
      newEmail: '新信箱',
      newEmailCode: '新信箱收到的驗證碼',
      oldEmailCode: '當前信箱收到的驗證碼',
      currentPassword: '當前密碼',
      newPassword: '新密碼',
      changePassword: '更換密碼',
      updatedPassword: '密碼已更新，其他設備已登出。',
      emailChanged: '信箱已更新',
      accountCard: '帳戶資料',
      emailCard: '信箱',
      securityCard: '安全設置',
      memberSince: '加入時間',
      defaultNicknameHint: '在此修改你的暱稱。',
      cancel: '取消',
      confirm: '確認',
      close: '關閉',
    },
    common: {
      loading: '載入中...',
      apply: '套用篩選',
      reset: '清除',
      error: '發生錯誤',
      retry: '重試',
      noData: '暫無數據',
      updated: '已更新',
      volume: '成交量',
      change: '漲跌',
      price: '價格',
      market: '市場',
      new: '新',
    },
    health: {
      title: '數據健康',
      checking: '檢查中…',
      ok: '正常',
      stale: '延遲',
      failed: '異常',
      unknown: '未採集',
      sourcesOk: '數據源正常',
      sourcesFailed: '個數據源異常',
      sourcesDelayed: '個數據源延遲',
      viewDetails: '查看詳情',
      close: '關閉',
      lastSuccessAge: '最近成功',
      lastSuccessAt: '成功時間',
      latency: '延遲',
      coverage: '覆蓋',
      missing: '缺失',
      consecutiveFailures: '連續失敗',
      autoRetry: '次，將自動重試',
      checkingStatus: '正在檢查數據源狀態…',
      noHealthRecords: '暫無健康記錄（採集器尚未寫入打點）',
      healthNote: '採集器每次成功/失敗會自動記錄；此處不展示頁面刷新時間，展示數據源真實更新時間。',
      secAgo: '秒前',
      minAgo: '分鐘前',
      hourAgo: '小時前',
      seconds: '秒',
      instant: '即時',
      items: '項',
      noCoverage: '—',
      descOk: '數據源按預期頻率更新',
      descStale: '超過設定閾值未更新，可能暫時不可用',
      descFailed: '最近一次採集失敗，系統自動重試中',
      descUnknown: '尚無打點記錄（服務啓動後自動產生）',
      total: '總數',
      serverTime: '伺服器時間',
      emptyDesc: '暫無健康記錄。採集器每次成功/失敗會自動寫入打點，服務重啓後幾分鐘內產生。',
      colMetric: '指標名稱',
      colMarket: '市場身份',
      colStatus: '狀態',
      colLastSuccess: '最近成功時間',
      colLatency: '延遲',
      colCoverage: '有效覆蓋',
      colMissing: '缺失區間 / 狀態原因',
      colRetry: '重試',
      normalUpdating: '正常更新',
      retryAuto: '自動',
      subtitle: '展示各數據源的真實更新狀態（非頁面刷新時間）。故障隻影響相關模組，不會整頁報錯。',
      footerNote: '來源名稱與閾值按數據類型配置（實時行情 / 小時序列 / 日曆不同）。',
      refresh: '刷新',
      dataStatus: '數據狀態',
      srcTickers: '實時行情',
      srcSpotTickers: '多所現貨報價',
      srcExternalTickers: '外部交易所報價',
      srcOverview: '市場概覽',
      srcDerivatives: '合約衍生數據',
      srcNews: '資訊流',
      srcCalendar: '經濟日曆',
      srcHlOnchain: 'Hyperliquid 鏈上',
      srcLiqEvents: '爆倉事件',
    },
    help: {
      title: '幫助中心',
      subtitle: '找到想用的功能、理解它解決什麼問題，並按 3–5 步完成操作。',
      searchPlaceholder: '搜尋功能…',
      allFamilies: '全部',
      entry: '入口位置',
      steps: '操作步驟',
      faq: '常見問題',
      types: '訊號與預警類型',
      backToAll: '返回全部',
      noResults: '沒有找到相符功能，請嘗試其他關鍵詞。',
      expandAll: '全部展開',
      collapseAll: '全部收起',
      cardCount: '張功能卡',
    },
    research: {
      tab: '分析',
      instrument: '標的',
      venue: '交易所',
      product: '產品',
      metric: '指標',
      range: '範圍',
      interval: '週期',
      qOk: '即時',
      qPartial: '部分',
      qStale: '延遲',
      qMissing: '無數據',
      qUnsupported: '不可用',
      qInsufficient: '歷史不足',
      qInsufficientDetail: '歷史未達質量門檻（樣本 ≥ 100 且覆蓋率 ≥ 80%）',
      coverage: '覆蓋率',
      asOf: '更新於',
      source: '來源',
      emptyTitle: '暫無分析數據',
      emptyDesc: '請選擇受支持的標的與指標以查看聯動序列。',
      metricPrice: '價格',
      metricPriceDesc: '最新成交價（USD），按標的爲現貨或永續。',
      metricQuoteVolume: '成交額',
      metricQuoteVolumeDesc: '區間名義成交額（USD）。',
      metricOpenInterest: '持倉量',
      metricOpenInterestDesc: '未平倉名義額（USD），區間末快照。',
      metricFundingRate: '資金費率',
      metricFundingRateDesc: '已結算資金費率（適用時爲 8h 週期）。',
      metricLiquidation: '爆倉',
      metricLiquidationDesc: '區間爆倉名義額合計（USD）。',
      metricRelativeReturn: '相對 BTC 收益',
      metricRelativeReturnDesc: '相對 BTC 的收益差（百分點）。',
      metricBreadth: '市場寬度',
      metricBreadthDesc: '上漲資產佔比（%）。',
      metricHv: '歷史波動率',
      metricHvDesc: '由 OHLC 計算的年化波動率（%）。',
      linkedSeries: '聯動序列',
      seriesPrice: '價格',
      seriesVolume: '成交額',
      seriesOi: '持倉',
      seriesFunding: '費率',
      seriesLiq: '爆倉',
    },
  },
  'zh-Hans': {
    appName: 'AiLumix',
    nav: {
      markets: '行情',
      discover: '发现',
      futures: '合约',
      signals: '信号',
      chart: '分析',
      news: '资讯',
      calendar: '日历',
      watchlist: '自选',
      alerts: '预警',
      portfolio: '资产账本',
      hyperliquid: '链上',
      research: '研究',
      ai: 'AI',
      more: '更多',
      notifications: '通知',
      settings: '设置',
      profile: '账户',
      workspace: '我的研究',
      help: '帮助中心',
      strategy: '策略',
    },
    markets: {
      title: '行情',
      overview: '市场概览',
      totalCap: '总市值',
      volume24h: '24H 成交额',
      btcDominance: 'BTC 占比',
      ethDominance: 'ETH 占比',
      fearGreed: '恐惧与贪婪指数',
      tabFavorites: '自选',
      tabSpot: '现货',
      tabFutures: '合约',
      tabNew: '新币榜',
      tabTrending: '热门榜',
      tabAll: '市值',
      ovTotalOi: '全网合约持仓量',
      ovLongShort: '全网多空比',
      ovLiquidation: '24H 爆仓总额',
      ovStablecap: '稳定币总市值',
      ovAvgFunding: '资金费率均值',
      breadth: '市场宽度',
      breadthUp: '涨',
      breadthDown: '跌',
      breadthFlat: '平',
      moreData: '更多市场数据',
      overviewCollapse: '市场概览',
      sortBy: '排序',
      assetLevelRank: '资产级市值（按基础资产去重）',
      marketLevelRank: '市场级合约（分交易所）',
      dataHealthHint: '多交易所覆盖范围与更新时间',
      onlyAnomalies: '仅看异常',
      undo: '撤销',
      removed: '已移除',
      research: '研究',
      colSymbol: '名称',
      colPrice: '价格',
      colChange: '24H 涨跌',
      colHigh: '24H 最高',
      colLow: '24H 最低',
      colVolume: '24H 成交额',
      searchPlaceholder: '搜索币种 / 交易对',
      noResults: '未找到结果',
      rankings: '排行榜',
      addWatch: '☆',
      added: '✓',
      loginRequired: '登录后可加入自选',
    },
    detail: {
      kline: '价格图表',
      orderBook: '委托簿',
      trades: '最新成交',
      marketStats: '市场统计',
      about: '关于',
      high24h: '24H 最高',
      low24h: '24H 最低',
      volume24h: '24H 成交量',
      marketCap: '市值',
      symbol: '交易对',
      baseAsset: '基础资产',
      quoteAsset: '计价资产',
      addWatchlist: '加入自选',
      addedWatchlist: '已加入自选',
      createAlert: '建立预警',
      buy: '买单',
      sell: '卖单',
      time: '时间',
      price: '价格',
      amount: '数量',
      derivatives: '合约数据',
      fundingRate: '资金费率',
      nextFunding: '下次结算',
      openInterest: '持仓量',
      openInterestUsd: '持仓量 (USD)',
      longShortRatio: '多空比',
      annualized: '年化',
      fundamentals: '币种基本面',
      rank: '排名',
      supply: '流通供应量',
      totalSupply: '总供应量',
      ath: '历史新高',
      atl: '历史新低',
      fundingHistory: '资金费率历史',
      oiHistory: '持仓量历史',
      contractSpec: '合约规格',
      leverage: '杠杆',
      minQty: '最小下单',
      tickSize: '最小价差',
      ctVal: '面值',
      history: '历史',
    },
    rank: {
      rankGainers: '涨幅榜',
      rankLosers: '跌幅榜',
      rankVolume: '成交额榜',
      rankTrending: '热门榜',
      rankNew: '新币榜',
      rankVolatility: '波动率榜',
      rankOi: '持仓量榜',
      rankFunding: '资金费率榜',
      rankColRank: '#',
      rankColSymbol: '交易对',
      rankColPrice: '价格',
      rankColChange: '24H %',
      rankColVolume: '24H 成交量',
      rankColFunding: '资金费率',
      rankColOi: '持仓量 (USD)',
      rankColRatio: '多空比',
    },
    watchlist: {
      title: '自选',
      newGroup: '新增分组',
      groupName: '分组名称',
      empty: '自选列表为空，请从行情页添加币种。',
      sessionExpired: '登录已过期，请重新登录',
      remove: '移除',
      addSymbol: '添加币种',
      symbolPlaceholder: '例如 BTCUSDT',
      defaultGroup: '默认',
      sort: '排序',
      sortManual: '手动',
      sortPrice: '价格',
      sortChange: '涨跌',
      sortVolume: '成交量',
      addCoin: '添加币种',
      groupEmpty: '此分组为空',
      deleteGroup: '删除分组',
      deleteGroupConfirm: '确认删除该分组？组内币种将被移除。',
      searchHint: '输入币种或交易对开始搜索',
      alreadyAdded: '已添加',
      add: '添加',
    },
    notifications: {
      title: '通知中心',
      markAllRead: '全部标为已读',
      all: '全部',
      unread: '未读',
      empty: '暂无通知',
      emptyUnread: '已全部读完',
      typePrice: '价格警报',
      typeSignal: '市场信号',
      typeNews: '新闻',
      typeSystem: '系统',
      typeAccount: '账户',
      typeAi: 'AI 提示',
      view: '查看',
      markRead: '标为已读',
      delete: '删除',
      loginRequired: '请先登录查看通知',
    },
    alerts: {
      title: '市场预警',
      newAlert: '新增预警',
      symbol: '交易对',
      condition: '条件',
      target: '目标值',
      status: '状态',
      active: '生效中',
      triggered: '已触发',
      disabled: '已停用',
      priceAbove: '价格高于',
      priceBelow: '价格低于',
      changeAbove: '涨跌幅高于',
      changeBelow: '涨跌幅低于',
      volumeSpike: '成交量突增',
      crossAbove: '上穿',
      crossBelow: '下穿',
      fundingAbove: '资金费率高于',
      fundingBelow: '资金费率低于',
      oiAbove: '持仓量高于',
      oiBelow: '持仓量低于',
      rsiAbove: 'RSI高于',
      rsiBelow: 'RSI低于',
      invalidNumber: '请输入有效数字',
      minValue: '不能小于',
      maxValue: '不能大于',
      create: '建立',
      cancel: '取消',
      loginRequired: '请先登录后建立预警。',
      error: '建立预警失败。',
      empty: '暂无预警，请点击「建立预警」。',
      filterAll: '全部',
      filterActive: '生效中',
      filterTriggered: '已触发',
      searchSymbol: '搜索交易对',
      deleteAlert: '删除',
      confirmDelete: '确认删除该预警？',
      confirmDiscard: '有未储存的变更，确定放弃？',
      summary: '当',
      browserAllowed: '已允许',
      browserDefault: '未授权',
      browserDenied: '已阻止',
      browserUnsupported: '不支援',
      groupPrice: '价格',
      groupChange: '涨跌',
      groupVolume: '成交量',
      groupCross: '穿越',
      groupFunding: '资金费率',
      groupOi: '持仓量',
      groupRsi: 'RSI',
      summaryValue: '目标值',
      interval: '计算周期',
      currentValue: '当前值',
      lastChecked: '最近评估',
      oneShot: '一次性',
      oneShotHint: '触发后自动停用',
      repeat: '重复',
      repeatHint: '触发后持续监控',
      noCurrent: '—',
      venueLabel: '交易所',
      productLabel: '产品',
      snapshot: '触发快照',
      pause: '暂停',
      resume: '恢复',
      edit: '编辑',
      update: '更新',
    },
    news: {
      title: '资讯',
      source: '来源',
      readMore: '阅读更多',
      expand: '展开',
      catLatest: '最新',
      catBreaking: '突发',
      catBitcoin: '比特币',
      catEthereum: '以太坊',
      catAltcoins: '山寨币',
      catMacro: '宏观',
      catExchange: '交易所',
      catRegulation: '监管',
      collapse: '收起',
      flashTip: '快讯式资讯流 — 摘要就地展开，不跳转新页。',
      empty: '暂无资讯。',
    },
    auth: {
      login: '登录',
      register: '注册',
      email: '信箱',
      password: '密码',
      confirmPassword: '确认密码',
      displayName: '暱称',
      logout: '登出',
      sessionExpired: '登录已失效 · 重新登录',
      welcomeBack: '欢迎回来',
      loginHint: '登录以管理自选、预警与持仓记录',
      alreadyLoggedIn: '你已登录',
      backToMarkets: '返回市场',
      goToAccount: '进入账户中心',
      loggingIn: '登录中...',
      createAccount: '建立账户',
      registering: '建立账户中...',
      noAccount: '没有账户？立即注册',
      hasAccount: '已有账户？立即登录',
      verificationCode: '验证码',
      sendCode: '发送验证码',
      resendIn: '{s} 秒后重发',
      codeSent: '验证码已发送',
      randomUsernameHint: '系统将自动生成用户名，之后可在账户中心修改。',
      emailPlaceholder: '请输入信箱',
      passwordPlaceholder: '请输入密码',
      confirmPasswordPlaceholder: '请再次输入密码',
      codePlaceholder: '请输入验证码',
    },
    portfolio: {
      title: '资产账本',
      addPosition: '新增持仓',
      symbol: '交易对',
      amount: '数量',
      avgBuyPrice: '平均买入价',
      currentValue: '当前价值',
      cost: '成本',
      pnl: '盈亏',
      allocation: '占比',
      totalValue: '总价值',
      totalCost: '总成本',
      totalPnl: '总盈亏',
      settledRate: '结算收益率',
      empty: '尚无持仓，请添加 BTC、ETH、SOL 等币种。',
      remove: '移除',
      loginRequired: '请先登录以管理资产。',
    },
    profile: {
      title: '账户与设定',
      language: '语言',
      theme: '主题',
      dark: '深色',
      light: '浅色',
      displayName: '暱称',
      email: '信箱',
      role: '角色',
      notificationChannel: '通知渠道',
      save: '储存',
      saved: '已储存',
      avatar: '头像',
      avatarDefault: '默认',
      avatarUpload: '上传头像',
      avatarUploadHint: '点击头像或下方按钮上传本地图片（PNG / JPG / WEBP / GIF，最大 3MB）。',
      avatarChange: '更换',
      avatarSaving: '上传中...',
      avatarInvalid: '仅支持 PNG、JPG、WEBP 或 GIF 图片（最大 3MB）。',
      changeEmail: '更换信箱',
      changeEmailHint: '将向当前信箱与新信箱各发送一封验证码邮件。',
      newEmail: '新信箱',
      newEmailCode: '新信箱收到的验证码',
      oldEmailCode: '当前信箱收到的验证码',
      currentPassword: '当前密码',
      newPassword: '新密码',
      changePassword: '更换密码',
      updatedPassword: '密码已更新，其他设备已登出。',
      emailChanged: '信箱已更新',
      accountCard: '账户资料',
      emailCard: '信箱',
      securityCard: '安全设置',
      memberSince: '加入时间',
      defaultNicknameHint: '在此修改你的暱称。',
      cancel: '取消',
      confirm: '确认',
      close: '关闭',
    },
    common: {
      loading: '载入中...',
      apply: '套用筛选',
      reset: '清除',
      error: '发生错误',
      retry: '重试',
      noData: '暂无数据',
      updated: '已更新',
      volume: '成交量',
      change: '涨跌',
      price: '价格',
      market: '市场',
      new: '新',
    },
    health: {
      title: '数据健康',
      checking: '检查中…',
      ok: '正常',
      stale: '延迟',
      failed: '异常',
      unknown: '未采集',
      sourcesOk: '数据源正常',
      sourcesFailed: '个数据源异常',
      sourcesDelayed: '个数据源延迟',
      viewDetails: '查看详情',
      close: '关闭',
      lastSuccessAge: '最近成功',
      lastSuccessAt: '成功时间',
      latency: '延迟',
      coverage: '覆盖',
      missing: '缺失',
      consecutiveFailures: '连续失败',
      autoRetry: '次，将自动重试',
      checkingStatus: '正在检查数据源状态…',
      noHealthRecords: '暂无健康记录（采集器尚未写入打点）',
      healthNote: '采集器每次成功/失败会自动记录；此处不展示页面刷新时间，展示数据源真实更新时间。',
      secAgo: '秒前',
      minAgo: '分钟前',
      hourAgo: '小时前',
      seconds: '秒',
      instant: '即时',
      items: '项',
      noCoverage: '—',
      descOk: '数据源按预期频率更新',
      descStale: '超过设定阈值未更新，可能暂时不可用',
      descFailed: '最近一次采集失败，系统自动重试中',
      descUnknown: '尚无打点记录（服务启动后自动产生）',
      total: '总数',
      serverTime: '伺服器时间',
      emptyDesc: '暂无健康记录。采集器每次成功/失败会自动写入打点，服务重启后几分钟内产生。',
      colMetric: '指标名称',
      colMarket: '市场身份',
      colStatus: '状态',
      colLastSuccess: '最近成功时间',
      colLatency: '延迟',
      colCoverage: '有效覆盖',
      colMissing: '缺失区间 / 状态原因',
      colRetry: '重试',
      normalUpdating: '正常更新',
      retryAuto: '自动',
      subtitle: '展示各数据源的真实更新状态（非页面刷新时间）。故障只影响相关模组，不会整页报错。',
      footerNote: '来源名称与阈值按数据类型配置（实时行情 / 小时序列 / 日历不同）。',
      refresh: '刷新',
      dataStatus: '数据状态',
      srcTickers: '实时行情',
      srcSpotTickers: '多所现货报价',
      srcExternalTickers: '外部交易所报价',
      srcOverview: '市场概览',
      srcDerivatives: '合约衍生数据',
      srcNews: '资讯流',
      srcCalendar: '经济日历',
      srcHlOnchain: 'Hyperliquid 链上',
      srcLiqEvents: '爆仓事件',
    },
    help: {
      title: '帮助中心',
      subtitle: '找到想用的功能、理解它解决什么问题，并按 3–5 步完成操作。',
      searchPlaceholder: '搜寻功能…',
      allFamilies: '全部',
      entry: '入口位置',
      steps: '操作步骤',
      faq: '常见问题',
      types: '讯号与预警类型',
      backToAll: '返回全部',
      noResults: '没有找到相符功能，请尝试其他关键词。',
      expandAll: '全部展开',
      collapseAll: '全部收起',
      cardCount: '张功能卡',
    },
    research: {
      tab: '分析',
      instrument: '标的',
      venue: '交易所',
      product: '产品',
      metric: '指标',
      range: '范围',
      interval: '周期',
      qOk: '即时',
      qPartial: '部分',
      qStale: '延迟',
      qMissing: '无数据',
      qUnsupported: '不可用',
      qInsufficient: '历史不足',
      qInsufficientDetail: '历史未达质量门槛（样本 ≥ 100 且覆盖率 ≥ 80%）',
      coverage: '覆盖率',
      asOf: '更新于',
      source: '来源',
      emptyTitle: '暂无分析数据',
      emptyDesc: '请选择受支持的标的与指标以查看联动序列。',
      metricPrice: '价格',
      metricPriceDesc: '最新成交价（USD），按标的为现货或永续。',
      metricQuoteVolume: '成交额',
      metricQuoteVolumeDesc: '区间名义成交额（USD）。',
      metricOpenInterest: '持仓量',
      metricOpenInterestDesc: '未平仓名义额（USD），区间末快照。',
      metricFundingRate: '资金费率',
      metricFundingRateDesc: '已结算资金费率（适用时为 8h 周期）。',
      metricLiquidation: '爆仓',
      metricLiquidationDesc: '区间爆仓名义额合计（USD）。',
      metricRelativeReturn: '相对 BTC 收益',
      metricRelativeReturnDesc: '相对 BTC 的收益差（百分点）。',
      metricBreadth: '市场宽度',
      metricBreadthDesc: '上涨资产占比（%）。',
      metricHv: '历史波动率',
      metricHvDesc: '由 OHLC 计算的年化波动率（%）。',
      linkedSeries: '联动序列',
      seriesPrice: '价格',
      seriesVolume: '成交额',
      seriesOi: '持仓',
      seriesFunding: '费率',
      seriesLiq: '爆仓',
    },
  },
};

export function translate(lang: Language): I18nDict {
  return copy[lang] ?? copy['en'];
}

export const LANGUAGES: Array<{ value: Language; label: string }> = [
  { value: 'en', label: 'English' },
    { value: 'zh-Hans', label: '简体中文' },
  { value: 'zh-Hant', label: '繁體中文' },
];
